<template>
  <div class="gm_page">
    <link
      rel="stylesheet"
      href="/assets/css/vendor/material-icons-outlined.css"
    />

    <!-- 1 — Hero -->
    <section class="gm_hero">
      <div class="gm_container gm_hero_row">
        <div class="gm_hero_left">
          <span class="gm_hero_badge">
            <i class="material-icons-outlined">verified</i>
            Trusted by {{ homepage_statistics.user_count | stats_format }} users
          </span>
          <h1 class="gm_hero_title">{{ $t('gig.hero_title') }}</h1>
          <p class="gm_hero_text">{{ $t('gig.hero_desc') }}</p>
          <div class="gm_hero_ctas">
            <b-link
              :to="{ name: 'allGigMissionListing' }"
              target="_blank"
              class="gm_btn gm_btn_blue"
            >{{ $t('gig.browse_gigs') }}</b-link>
            <button
              type="button"
              class="gm_btn gm_btn_outline_pink"
              v-b-modal.modal-gig-app
            >{{ $t('gig.post_a_gig') }}</button>
          </div>
        </div>
        <div class="gm_hero_right">
          <img
            :src="webUrl + 'assets/jobeekerImages/gig-missions.webp'"
            alt="Gig Missions"
            class="gm_hero_img"
            loading="lazy"
          />
        </div>
      </div>
    </section>

    <!-- 2 — What Is Gig Missions? -->
    <section class="gm_section gm_about">
      <div class="gm_container">
        <div class="gm_section_head_center">
          <h2 class="gm_section_heading">{{ $t('gig.what_is') }}</h2>
          <p class="gm_section_subtitle">{{ $t('gig.what_is_desc') }}</p>
        </div>
        <div class="gm_cards_grid">
          <div
            v-for="(feature, index) in features"
            :key="index"
            class="gm_card"
            :class="feature.cardClass"
          >
            <i v-if="feature.decor" class="material-icons-outlined gm_card_decor">{{
              feature.decor
            }}</i>
            <div class="gm_card_body">
              <div v-if="feature.icon" class="gm_card_icon" :class="feature.iconClass">
                <i class="material-icons-outlined">{{ feature.icon }}</i>
              </div>
              <h3 class="gm_card_title">{{ feature.title }}</h3>
              <p class="gm_card_text">{{ feature.text }}</p>
              <b-link
                v-if="feature.link"
                :to="{ name: feature.link.name }"
                target="_blank"
                class="gm_card_link"
              >
                {{ feature.link.text }}
                <i class="material-icons-outlined">east</i>
              </b-link>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 3 — Two Ways To Use Gig Missions -->
    <section class="gm_section gm_lifecycle">
      <div class="gm_container">
        <div class="gm_section_head_center">
          <h2 class="gm_section_heading">{{ $t('gig.two_ways') }}</h2>
          <p class="gm_section_subtitle">{{ $t('gig.two_ways_desc') }}</p>
        </div>
        <div class="gm_ways_grid">
          <div v-for="(step, index) in steps" :key="index" class="gm_way_card">
            <div class="gm_way_top_icon" :class="step.iconClass">
              <i class="material-icons-outlined">{{ step.icon }}</i>
            </div>
            <h3 class="gm_way_title">{{ step.title }}</h3>
            <p class="gm_way_desc">{{ step.text }}</p>
            <div class="gm_way_row gm_way_workers_row">
              <div class="gm_way_row_content">
                <span class="gm_way_badge gm_way_badge_blue">
                  <i class="material-icons-outlined">person</i>{{ $t('gig.for_workers') }}</span>
                <p class="gm_way_row_text">{{ step.workersText }}</p>
              </div>
            </div>
            <div class="gm_way_row gm_way_poster_row">
              <div class="gm_way_row_content">
                <span class="gm_way_badge gm_way_badge_pink">
                  <i class="material-icons-outlined">control_point</i>{{ $t('gig.for_poster') }}</span>
                <p class="gm_way_row_text">{{ step.posterText }}</p>
              </div>
            </div>
            <div class="gm_way_footer">
              <span class="gm_step_label" :class="step.labelClass">{{ step.label }}</span>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 4 — Bottom CTA banner -->
    <section class="gm_cta_banner">
      <div class="gm_container">
        <div class="gm_cta_inner">
          <h2 class="gm_cta_title">{{ $t('gig.ready_title') }}</h2>
          <p class="gm_cta_text">{{ $t('gig.ready_desc') }}</p>
          <div class="gm_cta_btns">
          <b-link
            :to="{ name: 'signup' }"
            target="_blank"
            class="gm_btn gm_btn_white"
          >{{ $t('gig.create_account') }}</b-link>
          <b-link
            :to="{ name: 'allGigMissionListing' }"
            target="_blank"
            class="gm_btn gm_btn_outline_white"
          >{{ $t('gig.get_started') }}</b-link>
          </div>
        </div>
      </div>
    </section>

    <!-- 5 — App Section -->
    <section class="gm_section">
      <div class="gm_container">
        <div class="gm_section_head_center">
          <h2 class="gm_section_heading">{{ $t('gig.manage_everything') }}</h2>
          <p class="gm_section_subtitle">{{ $t('gig.manage_everything_desc') }}</p>
        </div>
        <div class="gm_app_row gm_negative_50px">
          <!-- Left: download links -->
          <div class="gm_app_left">
            <div class="gm_app_download">
              <a target="_blank" href="https://onelink.to/pkye4c" class="gm_app_qr_wrap">
                <img :src="webUrl + 'assets/images/image-561.webp'" class="gm_app_qr" alt="Scan to download">
              </a>
              <div class="gm_app_badges">
                <a v-bind:href="get_ios_link.option_value" target="_blank">
                  <img :src="webUrl + 'assets/images/image-550.webp'" class="gm_app_badge" alt="Download on the App Store">
                </a>
                <a v-bind:href="get_android_link.option_value" target="_blank">
                  <img :src="webUrl + 'assets/images/image-551.webp'" class="gm_app_badge" alt="Get it on Google Play">
                </a>
              </div>
            </div>
          </div>
          <!-- Right: auto slideshow -->
          <div class="gm_app_right">
            <div class="gm_app_slides_wrap">
              <img
                v-for="(frame, i) in appFrames"
                :key="i"
                :src="webUrl + frame"
                class="gm_app_slide_img"
                :class="{ gm_app_slide_active: appSlideIndex === i }"
                alt="App feature"
              >
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Post a Gig — Download App popup -->
    <b-modal id="modal-gig-app" hide-header centered ref="gig-app-modal" title="" hide-footer content-class="common_model_header common_model_title" modal-class="background_custom_class rewards_pop_up">
      <div class="reward_modal_whole_div">
        <div class="left_gradient_div_rewards">
          <div class="button_div_rewards mobile_reward_btn">
            <button type="button" aria-label="Close" class="close" @click="closeGigAppModel()">×</button>
          </div>
          <div class="reward_cut_img">
            <img :src="webUrl+'assets/images/Group-808.webp'">
          </div>
        </div>
        <div class="right_content_div_rewards">
          <div class="button_div_rewards desktop_reward_btn">
            <button type="button" aria-label="Close" class="close" @click="closeGigAppModel()">×</button>
          </div>
          <div class="inner_content_rewards">
            <h2>{{ $t('app.download_our_app') }}</h2>
            <p class="desktop_reward_btn">{{ $t('app.app_desc') }}</p>
            <p class="mobile_reward_btn">{{ $t('app.app_desc_l1') }}<br>{{ $t('app.app_desc_l2') }}<br>{{ $t('app.app_desc_l3') }}</p>
            <div class="whole_icon_rewards">
              <div class="qr_div_reward">
                <a target="_blank" href="https://onelink.to/pkye4c"><img :src="webUrl+'assets/images/image-560.webp'" class="qr_rewards"></a>
              </div>
              <div class="store_div_rewards">
                <a v-bind:href="
                    (get_ios_link.option_key = 'ios_ptuser_link'
                    ? get_ios_link.option_value
                    : get_ios_link.option_value)
                  " target="_blank">
                  <img :src="webUrl+'assets/images/image-550.webp'" class="apple_rewards">
                </a>
                <br>
                <a v-bind:href="
                    (get_android_link.option_key = 'android_ptuser_link'
                    ? get_android_link.option_value
                    : get_android_link.option_value)
                  " target="_blank">
                  <img :src="webUrl+'assets/images/image-551.webp'" class="play_rewards">
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { POST_API } from "../../store/actions.type";
import { getMetaInfo } from '@/seo.config';

export default {
  metaInfo() { return getMetaInfo('/gig-missions'); },
  data() {
    return {
      webUrl: process.env.VUE_APP_URL,
      get_ios_link: [],
      get_android_link: [],
      homepage_statistics: {},
      appSlideIndex: 0,
      appSlideTimer: null,
      // Per-country slides (config/countries.js gigAppSlides): SG English, TH Thai.
      appFrames: this.$profile.gigAppSlides,
    };
  },
  computed: {
    // Static display config. Lives in computed (not data) so the strings resolve
    // through $t() and follow the active locale — THAILAND.md §11 Phase 1.
    features() {
      return [
        { icon: "bolt", iconClass: "gm_icon_blue", cardClass: "gm_card_wide gm_card_light", decor: "hub",
          title: this.$t("gig.f1_title"), text: this.$t("gig.f1_text") },
        { icon: "swap_horiz", iconClass: "gm_icon_pink", cardClass: "",
          title: this.$t("gig.f2_title"), text: this.$t("gig.f2_text") },
        { icon: "paid", iconClass: "gm_icon_navy", cardClass: "",
          title: this.$t("gig.f3_title"), text: this.$t("gig.f3_text") },
        { icon: null, cardClass: "gm_card_wide gm_card_dark", decor: "fact_check",
          title: this.$t("gig.f4_title"), text: this.$t("gig.f4_text"),
          link: { name: "allGigMissionListing", text: this.$t("gig.browse_gig_listings") } },
      ];
    },
    steps() {
      return [
        { icon: "person_add", iconClass: "gm_way_icon_blue",
          title: this.$t("gig.s1_title"), text: this.$t("gig.s1_text"),
          workersText: this.$t("gig.s1_workers"), posterText: this.$t("gig.s1_poster"),
          label: this.$t("gig.s1_label"), labelClass: "gm_step_label_blue" },
        { icon: "search", iconClass: "gm_way_icon_purple",
          title: this.$t("gig.s2_title"), text: this.$t("gig.s2_text"),
          workersText: this.$t("gig.s2_workers"), posterText: this.$t("gig.s2_poster"),
          label: this.$t("gig.s2_label"), labelClass: "gm_step_label_purple" },
        { icon: "fact_check", iconClass: "gm_way_icon_pink",
          title: this.$t("gig.s3_title"), text: this.$t("gig.s3_text"),
          workersText: this.$t("gig.s3_workers"), posterText: this.$t("gig.s3_poster"),
          label: this.$t("gig.s3_label"), labelClass: "gm_step_label_pink" },
      ];
    },
  },
  filters: {
    stats_format: function(value) {
      if (!value) return '0'
      value = value.toString()
      if(value >= 1000 && value < 1000000){
        return value < 10000
          ? (value/1000).toFixed(1) + 'K ++'
          : Math.round(value/1000) + 'K ++';
      }
      else if(value >= 1000000){
        return (value/1000000).toFixed(1) + 'M ++';
      }
      else{
        return value;
      }
    },
  },
  methods: {
    getHomepageData() {
      return this.$store
        .dispatch(POST_API, {
          data: {},
          api: "/api/homepage-data",
        })
        .then(() => {
          if (!this.$store.getters.containsErrors) {
            this.homepage_statistics = this.$store.getters.getResults.data;
          }
        });
    },
    getFooterData() {
      return this.$store
        .dispatch(POST_API, {
          api: "/api/footer-data",
        })
        .then(() => {
          if (this.$store.getters.containsErrors) {
            return [];
          } else {
            this.get_android_link = this.$store.getters.getResults.get_android_link[0];
            this.get_ios_link = this.$store.getters.getResults.get_ios_link[0];
          }
        });
    },
    closeGigAppModel() {
      this.$refs["gig-app-modal"].hide();
    },
    startAppSlideshow() {
      this.appSlideTimer = setInterval(() => {
        this.appSlideIndex = (this.appSlideIndex + 1) % this.appFrames.length;
      }, 3500);
    },
    goToSlide(i) {
      this.appSlideIndex = i;
      clearInterval(this.appSlideTimer);
      this.startAppSlideshow();
    },
  },
  mounted() {
    this.startAppSlideshow();
    Promise.all([
      this.get_android_link.length ? Promise.resolve() : this.getFooterData(),
      this.getHomepageData(),
    ]).finally(() => {
      document.dispatchEvent(new Event('x-app-rendered'));
    });
  },
  beforeDestroy() {
    clearInterval(this.appSlideTimer);
  },
};
</script>

<style scoped>
@import "/assets/css/common-font-family.css";
@import "/assets/css/common-footer.css";
@import "/assets/css/gig-missions.css";
</style>
